import { generatedEnumData } from "../codegen/enumerations";
import { generatedIrEnumData } from "../codegen/enumerationsIr";
import { EnumMetadata, EnumValueMetadata } from "./model";
import { Translations } from "./Translations";
import { Language } from "../model";

/**
 * Provides metadata and other helpers for enumerations.
 */
export class EnumerationsLogic {

  /**
   * Gets a label text for an enumeration value.
   * @param enumType Name of the Type of the enumeration with or without the namespace. The default namespace for the type is SALAXY.ENUM.
   * @param memberName Name of the member for which the label is fetched.
   * Also supports the value of the member if there is only difference in casing (which is the normal convention).
   * @param defaultValue The default value if the translation is not found.
   * Supports special keys "#name" and "name" to return the member name either with or without hash in the beginning.
   * Default is "#name", which returns "#[MemberName]" if the translation is not found.
   * @param language Optional language, for forcing the result as given language.
   * @returns Language specific text form translations or the memberName text if no translation is found.
   */
  public static getEnumLabel(enumType: string, memberName: string, defaultValue: undefined | string | "#name" | "name" = "#name", language: Language | null = null): string {
    if (!enumType || !memberName) {
      return `#err ${enumType || "NoEnumType"}.${memberName || "NoEnumValue"}`;
    }
    memberName = this.lowerCamelCase(memberName);
    if (defaultValue === "name") {
      defaultValue = this.upperCamelCase(memberName);
    } else if (defaultValue === "#name") {
      defaultValue = this.upperCamelCase(memberName);
    }

    const label = Translations.getWithDefault(`${this.getFullEnumType(enumType)}.${memberName}.label`,undefined, language) || defaultValue;
    if (!label) {
      return this.upperCamelCase(memberName);
    }
    return label;
  }

  /**
   * Gets a description text for an enumeration value in current language.
   * @param enumType - Name of the Type of the enumeration with or without the namespace. The default namespace for the type is SALAXY.ENUM.
   * @param memberName Name of the member for which the description is fetched.
   * Also supports the value of the member if there is only difference in casing (which is the normal convention).
   * @param defaultValue The default value if the translation is not found. Default is null.
   * Supports special keys "#name" and "name" to return the member name either with or without hash in the beginning.
   * @param language Optional language, for forcing the result as given language.
   * @returns Language specific text form translations or null if none is found.
   */
  public static getEnumDescr(enumType: string, memberName: string, defaultValue?: undefined | string | "#name" | "name", language: Language | null = null): string | undefined {
    if (!enumType || !memberName) {
      return `#err ${enumType || "NoEnumType"}.${memberName || "NoEnumValue"}`;
    }
    memberName = this.lowerCamelCase(memberName);
    if (defaultValue === "name") {
      defaultValue = this.upperCamelCase(memberName);
    } else if (defaultValue === "#name") {
      defaultValue = this.upperCamelCase(memberName);
    }
    return Translations.getWithDefault(`${this.getFullEnumType(enumType)}.${memberName}.description`, undefined, language) || defaultValue;
  }

  /**
   * Gets a very short user interface text for an enumeration.
   * Currently only suppotrted for Unit, but we expect more of these.
   * If the texts will be language-versioned, that logic will be here as well.
   * @param enumType - Name of the Type of the enumeration.
   * @param enumValue - Value for which the text is fetched.
   */
  public static getEnumShortText(enumType: string, enumValue: string): string {
    if (!enumType || !enumValue) {
      return `#err ${enumType || "NoEnumType"}.${enumValue || "NoEnumValue"}`;
    }
    enumType = this.upperCamelCase(enumType);
    enumValue = this.lowerCamelCase(enumValue);
    switch (enumType) {
      case "Unit": {
        const unitTexts = {
          undefined: "kpl?",
          hour: "h",
          day: "pv",
          week: "vko",
          period: "jaksoa",
          one: "",
          count: "kpl",
          percent: "%",
          kilometers: "km",
        };
        return unitTexts[enumValue];
      }
      default: {
        throw new Error(`Enum type ${enumType} is not supported in getEnumShortText().`);
      }
    }
  }

  /**
   * Gets the metadata (texts etc.) for an enumeration value.
   * @param enumType -  Name of the Type of the enumeration with or without the namespace. The default namespace for the type is SALAXY.ENUM.
   * @param enumValue - Value for which the label is fetched.
   * @param language Optional language, for forcing the result as given language.
   */
  public static getEnumValueMetadata(enumType: string, enumValue: string, language: Language | null = null): EnumValueMetadata | undefined {
    const typeMetaData = this.getEnumMetadata(enumType, language);
    if (!enumValue || !typeMetaData) {
      return undefined;
    }
    enumValue = this.lowerCamelCase(enumValue);
    const enumValueObj = typeMetaData.values.filter((x) => x.name === enumValue)[0];
    return enumValueObj;
  }

  /**
   * Gets the metadata (texts etc.) for an enumeration and its values.
   * @param enumType -  Name of the Type of the enumeration with or without the namespace. The default namespace for the type is SALAXY.ENUM.
   * @param language Optional language, for forcing the result as given language.
   */
  public static getEnumMetadata(enumType: string, language: Language | null = null): EnumMetadata | undefined {
    if (!enumType) {
      return undefined;
    }
    const fullEnumType = this.getFullEnumType(enumType);
    const enumNames = fullEnumType.split(".");
    if (enumNames.length !== 3) {
      console.warn(`Invalid enum type ${enumType}`);
      return undefined;
    }
    const enumName = enumNames[2];
    const enumData = this.getGeneratedEnumData(`${enumNames[0]}.${enumNames[1]}`);
    if (!enumData) {
      console.warn(`Invalid enum namespace ${enumType}`);
      return undefined;
    }

    let enumObj = enumData.filter((x) => x.name === enumName)[0];
    const override = this.enumDataOverride.filter((x) => x.name === enumName)[0];
    enumObj = override || enumObj;
    if (!enumObj) {
      console.warn(`No enum metadata for  ${enumType}`);
      return undefined;
    }

    const result: EnumMetadata = {
      name: enumObj.name,
      descr: enumObj.description,
      values: enumObj.values.map((x) => {
        return {
          name: x.name,
          order: x.order,
          label: this.getEnumLabel(fullEnumType, x.name, undefined, language),
          descr: this.getEnumDescr(fullEnumType, x.name, undefined, language),
        };
      }),
    };
    return result;
  }

  /**
   * Gets the currently known enumerations.
   * @param ns - Namespace for the enumeration. By default this is SALAXY.ENUM
   */
  public static getEnums(ns ="SALAXY.ENUM"): string[] {
    return (this.getGeneratedEnumData(ns) ?? []).map((x) => x.name);
  }

  private static getGeneratedEnumData(ns = "SALAXY.ENUM") {
    switch(ns) {
      case "SALAXY.ENUM":
        return generatedEnumData;
      case "SALAXY.ENUM_IR":
        return generatedIrEnumData;
      default:
        return null;
    }
  }

  private static getFullEnumType(enumType: string): string {
    return enumType.indexOf(".") >= 0 ? enumType : `SALAXY.ENUM.${this.upperCamelCase(enumType)}`;
  }

  /**
   * You can override values of generatedEnumData without generating the entire set of enums from server.
   * TODO: These enums (if any) should be moved to C# code server-side and removed from here.
   */
  private static enumDataOverride: any[] = [{
    name: "NewEnumName",
    description: null,
    values: [
      { order: 0, name: "undefined" },
      { order: 1, name: "enum1" },
    ],
  },{
    name: "Culture",
    description: "Defines the supported cultures in the user interfaces and logic.",
    values: [
      { order: 0, name: "fi" },
      { order: 1, name: "en" },
    ],
  },{
    name: "SitemapVisibility",
    description: "Different values for visibility of sitemap node.",
    values: [
      { order: 0, name: "default" },
      { order: 1, name: "visible" },
      { order: 2, name: "hidden" },
      { order: 3, name: "readOnly" },
      { order: 4, name: "hiddenReadOnly" },
      { order: 5, name: "disabled" },
    ],
  },{
    name: "WorkflowEventUi",
    description: "This is really 'BootstrapStyle' on server-side code. WorkflowEventUi is autogen. Should be fixed: Added to GetApiEnumTypes.",
    values: [
      { order: 0, name: "default" },
      { order: 1, name: "primary" },
      { order: 2, name: "success" },
      { order: 3, name: "info" },
      { order: 4, name: "warning" },
      { order: 5, name: "danger" },
    ],
  },{
    name: "ExpensesVatRate",
    description: "VAT rates for expenses",
    values: [
      { order: 0, name: "undefined" },
      { order: 1, name: "tax255" },
      { order: 2, name: "tax24" },
      { order: 3, name: "tax14" },
      { order: 4, name: "tax135" },
      { order: 5, name: "tax10" },
      { order: 6, name: "tax0" },
      { order: 7, name: "none" },
      { order: 8, name: "other" },
      { order: 9, name: "multiple" },
    ],
  },
  ];

  private static lowerCamelCase(text: string) {
    text = "" + text; // Ensure it is a string
    return text.substr(0, 1).toLowerCase() + text.substr(1);
  }

  private static upperCamelCase(text: string) {
    text = "" + text; // Ensure it is a string
    return text.substr(0, 1).toUpperCase() + text.substr(1);
  }

}
